import React, { FC, memo } from 'react'
import { Col, FlexboxGrid, Placeholder } from 'rsuite'
import { useRouter } from 'next/router'
import cloneDeep from 'lodash/cloneDeep'

import useTypesenseProducts from '../../services/useTypesenseProducts'
import Carousel from '../Layouts/Carousel'
import { getAllChildNodes } from '../../lib/catalogFilter'
import { getTextContentByType } from '../../lib/text'
import useCategoryDataStructure from '../hooks/useCategoryDataStructure'
import CategoryCard from './CategoryCard'
import { QueryParam } from '../../utils/constants'
import { removeHomeCategory } from '../../lib/category'
import useProductCatalogCategoryContent from '../../services/strapi/useProductCatalogCategoryContent'
import useIsOnMobile from '../../services/useIsOnMobile'

import styles from '../../styles/CategoryCarousel.module.less'

interface CategoryCarouselProps {
  hideDescription?: boolean
  supplierCode?: string
  facetCounts?: Record<string, string>
}

const CategoryCarousel: FC<CategoryCarouselProps> = (props) => {
  const {
    supplierCode,
    hideDescription = false,
    facetCounts,
  } = props
  const { query } = useRouter()

  const { subCategory, selectedCategory, categories } = useCategoryDataStructure()

  const queryObject = cloneDeep(query)
  const categoryCode = Array.isArray(queryObject[QueryParam.filters])
    ? queryObject[QueryParam.filters]?.reverse()
      .find((path: string) => path.startsWith('c_')) || ''
    : ''

  const { productCatalogCategoryDescription } = useProductCatalogCategoryContent(categoryCode)

  const {
    isLoading,
  } = useTypesenseProducts()

  const isOnMobile = useIsOnMobile()

  const { current, childNodes } = getAllChildNodes(subCategory, selectedCategory)
  const description = current ? getTextContentByType(current, 'long') : null

  const renderedCategoryData = selectedCategory ? childNodes : removeHomeCategory(categories)

  return (
    <div className={styles['category-carousel-wrapper']}>
      {!!description && !hideDescription && (
        <FlexboxGrid>
          <FlexboxGrid.Item
            as={Col}
            xs={24}
            className="padding-zero"
          >
            <p className={`margin-top-spacer ${styles.description}`}>
              {description}
            </p>
            {productCatalogCategoryDescription && (
            <div className={`margin-top-spacer ${styles.description}`}>
              {productCatalogCategoryDescription}
            </div>
            )}
          </FlexboxGrid.Item>
        </FlexboxGrid>
      )}
      {isLoading && (
        <FlexboxGrid className={styles['skeleton-wrapper']}>
          {Array.from({ length: isOnMobile ? 2 : 4 }, (_, i) => (
            <Placeholder.Graph
              active
              key={i}
              className={styles['skeleton-category-card']}
            />
          ))}
        </FlexboxGrid>
      )}
      {!!renderedCategoryData?.length && !isLoading && (
        <FlexboxGrid className={styles['category-carousel']}>
          <Carousel reRenderDependency={isOnMobile}>
            {renderedCategoryData.map((cat) => {
              const hasProducts = facetCounts && facetCounts[cat['catalog.label']]
              const url = supplierCode ? `/supplier/${supplierCode}` : '/products'

              // hide categories with 0 products
              if (!hasProducts) return null

              return (
                <CategoryCard
                  key={cat['catalog.code']}
                  categoryName={cat['catalog.label']}
                  value={cat['catalog.code']}
                  media={cat.media}
                  url={url}
                />
              )
            })}
          </Carousel>
        </FlexboxGrid>
      )}
    </div>
  )
}

export default memo(CategoryCarousel)
